import deepmerge from 'deepmerge';
import { createContext, useMemo, useState } from 'react';
import type React from 'react';
import type { DeepPartial } from '~/types';

export type ContextType = {
  experimentsLoaded: boolean;
  headerHeight: number;
  headerLoaded: boolean;
  isBreadcrumbScrolledOut: boolean;
  isLazyLoadEnabled: boolean;
  setExperimentsLoaded: React.Dispatch<React.SetStateAction<boolean>>;
  setHeaderHeight: React.Dispatch<React.SetStateAction<number>>;
  setHeaderLoaded: React.Dispatch<React.SetStateAction<boolean>>;
  setIsBreadcrumbScrolledOut: React.Dispatch<React.SetStateAction<boolean>>;
  setLazyLoadEnabled: React.Dispatch<React.SetStateAction<boolean>>;
};

const defaultContext: ContextType = {
  experimentsLoaded: false,
  headerHeight: 112,
  headerLoaded: false,
  isBreadcrumbScrolledOut: false,
  isLazyLoadEnabled: false,
  setExperimentsLoaded() {},
  setHeaderHeight() {},
  setHeaderLoaded() {},
  setIsBreadcrumbScrolledOut() {},
  setLazyLoadEnabled() {}
};

const GlobalContext = createContext(defaultContext);

export type OwnProps = React.PropsWithChildren<{
  isLazyLoadEnabled: boolean;
}>;

export function GlobalContextProvider({ children, ...props }: OwnProps) {
  const [experimentsLoaded, setExperimentsLoaded] = useState(
    defaultContext.experimentsLoaded
  );
  const [headerHeight, setHeaderHeight] = useState<number>(
    defaultContext.headerHeight
  );
  const [headerLoaded, setHeaderLoaded] = useState(defaultContext.headerLoaded);
  const [isBreadcrumbScrolledOut, setIsBreadcrumbScrolledOut] = useState(
    defaultContext.isBreadcrumbScrolledOut
  );
  const [isLazyLoadEnabled, setLazyLoadEnabled] = useState(
    props.isLazyLoadEnabled
  );

  const value = useMemo<ContextType>(
    () => ({
      experimentsLoaded,
      headerHeight,
      headerLoaded,
      isBreadcrumbScrolledOut,
      isLazyLoadEnabled,
      setExperimentsLoaded,
      setHeaderHeight,
      setHeaderLoaded,
      setIsBreadcrumbScrolledOut,
      setLazyLoadEnabled
    }),
    [
      experimentsLoaded,
      headerHeight,
      headerLoaded,
      isBreadcrumbScrolledOut,
      isLazyLoadEnabled
    ]
  );

  return (
    <GlobalContext.Provider value={value}>{children}</GlobalContext.Provider>
  );
}

GlobalContextProvider.displayName = 'GlobalContextProvider';

export const generateGlobalContext = (
  customState: DeepPartial<ContextType>
): ContextType =>
  deepmerge<ContextType, DeepPartial<ContextType>>(defaultContext, customState);

export default GlobalContext;
